import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as echarts from "echarts";
import {
  Typography,
  Divider,
  Card,
  Flex,
  Tabs,
  Radio,
  Select,
  Space,
  Row,
  Col,
} from "antd";
import { MapCA } from "./MapChart";
import { LineScatterChart } from "./LineChart";
import { HiveLogo } from "./logo";
const Wrapper = styled.div`
  margin: 64px 32px;
`;
const { Text } = Typography;
const PrimaryColor = "RGB(39,78,130)";
const { TabPane } = Tabs;
const { Option } = Select;

const hc_color = "#D90429";
const who_color = "#F4A261";
const no_color = "#D3D3D3";
const risk_highlight_color = "RGB(213,106,88)";
const unknown_label = "Unknown";
const map_gradient = [
  "#f7f7f7",
  "#d9d9d9",
  "#bdbdbd",
  "#969696",
  "#737373",
  "#525252",
  "#252525",
];

const text_title_gold = "#996729";
const text_light_gray = "#9E9E9E";
const text_dark_gray = "#757575";

function formatNumberWithCommas(num: number): string {
  return num.toLocaleString("en-US");
}

const isValidNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value);

const formatCount = (value) =>
  isValidNumber(value) ? formatNumberWithCommas(value) : unknown_label;

const formatPercent = (value) =>
  isValidNumber(value) ? `${value}%` : unknown_label;

const buildDonutData = (value, yesColor) => {
  if (!isValidNumber(value)) {
    return {
      data: [{ value: 1, name: unknown_label }],
      colors: [no_color],
      centerText: unknown_label,
    };
  }

  return {
    data: [
      { value, name: "Yes" },
      { value: Math.max(0, 100 - value), name: "No" },
    ],
    colors: [yesColor, no_color],
    centerText: null,
  };
};

const getExceedanceColor = (value) =>
  isValidNumber(value) && value > 0 ? risk_highlight_color : PrimaryColor;

const hexToRgb = (hex) => {
  // Convert hex to RGB format
  let r = 0,
    g = 0,
    b = 0;

  // 3 digits
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // 6 digits
  else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }

  return { r, g, b };
};

const getColorForFailure = (value, maxColorHex) => {
  const percentage = parseFloat(value.replace("%", "")); // Extract the numeric value from the percentage string
  if (Number.isNaN(percentage)) {
    return no_color;
  }
  const maxColor = hexToRgb(maxColorHex); // Convert hex color to RGB

  // Interpolate from white to the max color
  const r = Math.round(255 - (255 - maxColor.r) * (percentage / 100)); // Interpolate red channel
  const g = Math.round(255 - (255 - maxColor.g) * (percentage / 100)); // Interpolate green channel
  const b = Math.round(255 - (255 - maxColor.b) * (percentage / 100)); // Interpolate blue channel

  // Return the color in RGB format
  return `rgb(${r}, ${g}, ${b})`;
};

// main data set
const who_hc_data = {
  Simcoe: {
    description: `All of the results for the 13 tested units in Simcoe County, Ontario, were below both the national and WHO guideline, with the highest at only 50 Bq/m³. 
    
    Ontario has not conducted province-wide radon testing at social housing locations. Data is only available from select municiaplities whose houisng authorities conducted testing and provided their data.
    `,
    all_tests: 13,
    HC: {
      exceedances: 0,
      failure_rate: 0,
    },
    WHO: {
      exceedances: 0,
      failure_rate: 0,
    },
    table: [],
  },
  Wellington: {
    description: `In Wellington County, which includes Guelph along with other municipalities, only 31 out of 396 buildings (7.8 per cent) exceeded the WHO guideline and just over one per cent (five units) exceeded Health Canada’s. However, 20 out of the 31 units (63.5 per cent) that tested above WHO guidelines were never mitigated. Neither was one of the five units that tested above Health Canada’s. 
    
    Ontario has not conducted province-wide radon testing at social housing locations. Data is only available from select municiaplities whose houisng authorities conducted testing and provided their data. 
    `,
    all_tests: 407,
    HC: {
      exceedances: 5,
      failure_rate: 1.23,
    },
    WHO: {
      exceedances: 31,
      failure_rate: 7.62,
    },
    table: [
      ["Winter 2015/2016", 226, 8.41, 0.88],
      ["Winter 2016/2017", 170, 7.06, 1.76],
    ],
  },
  Kingston: {
    description: `In Kingston, Ontario, 30 out of the 1,141 units, or 2.6 per cent, tested above the Health Canada guideline. 72 units of those units tested above WHO’s guideline.

    Nearly 10 per cent three of the 31 of the units that were remediated two years after the initial tests even though Health Canada recommends units testing between 200 Bq/m3 and 600 Bq/m3 be remediated in less than two years. One of those units was a townhouse that recorded 481 Bq/m3 - more than two times Health Canada’s guidelines. 
    
    Ontario has not conducted province-wide radon testing at social housing locations. Data is only available from select municiaplities whose houisng authorities conducted testing and provided their data. 
    `,
    all_tests: 1141,
    HC: {
      exceedances: 30,
      failure_rate: 2.63,
    },
    WHO: {
      exceedances: 72,
      failure_rate: 6.31,
    },
    table: [],
  },
  Montreal: {
    description: `In Montreal, data shows only 3,170 units – 2.3 per cent – have been tested for radon between 2018 and 2022. One unit in LaSalle recorded radon levels of 888 Bq/m3 - more than four times the Health Canada guideline.

    All units that are located on ground levels, where radon typically accumulates, have been tested, said the city’s municipal housing office.`,
    all_tests: 3107,
    HC: {
      exceedances: 19,
      failure_rate: 0.61,
    },
    WHO: {
      exceedances: 56,
      failure_rate: 1.8,
    },
    table: [],
  },
  BC: {
    description: `In British Columbia, a tenth of the 894 buildings tested exceeded Health Canada’s guidelines. B.C. Housing did not provide data on units that exceeded the WHO guideline or mitigation measures.

    The Capital Region Housing corporation, which covers the province’s capital of Victoria, does not conduct radon testing in its public housing units because "previous Provincial testing has shown the area to have low residential Radon levels below 200 Bq/m³," they said in a statement to the IJB.

    The province’s Ministry of Housing said in a statement they have begun remediation and "will be completing remediation for all sites with elevated levels." 

    “BC Housing is prioritizing remediating the sites that tested at the highest levels. If results exceed the levels set by Health Canada guidelines, BC Housing aims to meet the Health Canada timelines for mitigation (one to two years).”

    The Ministry also said it "follows Health Canada’s radon guidelines because they’re grounded in extensive risk assessments and national public consultation. Aligning with national guidance also supports harmonized building codes and empowers residents with practical tools to reduce exposure and safeguard indoor air quality."
    `,
    all_tests: 894,
    HC: {
      exceedances: 90,
      failure_rate: 10.07,
    },
    WHO: {
      exceedances: null,
      failure_rate: null,
    },
    table: [],
  },
  YK: {
    description: `In Yukon, 86 of the 532 tests were above Health Canada’s guidelines. However, nearly a third of the test results, 161 tests in total, were above the World Health Organization's guideline. 

    In a statement, Yukon Housing Corporation said it takes "steps to mitigate those radon levels for the safety of our tenants."

    "Specifically, we installed fans in the basements of units at no cost and provided radon monitors so tenants could monitor radon levels in their homes," said spokesperson Jason Seaton.

    He also added that "Yukon Housing’s radon safety guidelines are based on the guidelines used by Health Canada."`,
    all_tests: 532,
    HC: {
      exceedances: 86,
      failure_rate: 16.17,
    },
    WHO: {
      exceedances: 161,
      failure_rate: 30.26,
    },
    table: [],
  },
  NS: {
    description: `In Nova Scotia, 438 public housing units tested between 2008 and 2017 above Health Canada’s guidelines. 

    One unit in Stillwater Lake's recorded 270 Bq/m3 during the first test in 2015 while a test after mitigation recorded 577 Bq/m3 - almost three times the national guideline - in 2017.

    Six units in Nova Scotia tested over 1000 bq/m3, five times over the Health Canada guideline. highest measurement was 1434.6 bq/m3.

    The Nova Scotia Provincial Housing Agency did not respond to a request for comment.`,
    all_tests: 8086,
    HC: {
      exceedances: 438,
      failure_rate: 5.42,
    },
    WHO: {
      exceedances: null,
      failure_rate: null,
    },
    table: []
    // [ // We may need to get rid of the yearly failure distribution for Nova Scotia because we don’t have a true denominator so it’s impossible to derive an accurate failure rate.
    //   [2008, 112, 8.93, 0.0],
    //   [2009, 34, 20.59, 0.0],
    //   [2010, 56, 8.93, 0.0],
    //   [2014, 32, 21.88, 3.12],
    //   [2015, 13, 84.62, 76.92],
    //   [2016, 2, 50.0, 0.0],
    //   [2017, 1, 0.0, 0.0],
    // ],
  },
  MB: {
    description: `19 per cent of test results over six years in Manitoba exceeded the national guideline and 40 per cent exceeded the WHO’s guideline. 

    Heather Fisher lived in one of those homes. For more than 25 years, the 84-year-old pensioner has been in a small bungalow owned by Manitoba Housing in the town of Roblin, northwest of Winnipeg. 

    The home registered 466 Bq/m3 in a 2020 test.

    Fisher says she was never told the result.

    “I wish they would be a little bit more forthcoming with their results.” 

    It took Manitoba Housing about two years to install a system to vent the deadly gas out of her home and they’ve never done follow-up testing to confirm it’s working, Fisher says.

    Manitoba Housing said in a statement that buildings with radon levels above Health Canada’s guidelines “would have a radon mitigation system installed by a certified radon mitigation contractor within one year.” 
    `,
    all_tests: 1034,
    HC: {
      exceedances: 196,
      failure_rate: 18.96,
    },
    WHO: {
      exceedances: 410,
      failure_rate: 39.65,
    },
    table: [],
  },
  SK: {
    description: `Of the 509 test results from Saskatchewan, 10 per cent exceeded the Health Canada guideline and 30 per cent exceeded WHO’s. 

    “Saskatchewan Housing Corporation worked with local housing authorities to complete basic remediation,” the Ministry of Social Services said in a statement. “We encourage any tenants who wish to have radon testing completed to connect with their local housing authority to initiate testing in their property.” 

    The Ministery later added: "In March 2024, we announced a partnership with Lung Saskatchewan and the Saskatchewan Research Council to test radon levels in all Saskatchewan Housing Corporation units across the province over the next 10 years." 
    `,
    all_tests: 509,
    HC: {
      exceedances: 52,
      failure_rate: 10.22,
    },
    WHO: {
      exceedances: 151,
      failure_rate: 29.67,
    },
    table: [],
  },
  QC: {
    description: `In Montreal, data shows 3,179 units have been tested for radon between 2018 and 2022. One unit in LaSalle recorded radon levels of 888 Bq/m3 - more than four times the Health Canada guideline.

    Montreal's municipal housing office said that in all, 5,000 housing units have since been tested, representing ""representing 72% of our dwellings located at the lowest ground level,"" where radon typically emanates from and accumultates the most, and that 16 of the 18 units that exceeded Health Canada's guideline have been remediated and are now below the federal threshold.

    "Despite awareness efforts and explanations, only 58% of tenants granted us access to their homes to collect the dosimeters," said spokeperson Valérie Rhême.

    Seperately, *Quebec's provincial housing corporation said 90 per cent of buildings, 5,482 in total, have been tested for radon. Of those, seven per cent exceeded the federal guideline, but 27 per cent were above the World Health Organization's guideline. 

    "Mitigation work has been carried out where required," said the statement. 

    *Quebec's data is not included in this graphic as the type of data provided by the provincial body differs from that of the city of Montreal. Quebec's housing agency provided data on the number of buildings tested, while Montreal provided granular test results. 
    `,
    all_tests: 3179,
    HC: {
      exceedances: 20,
      failure_rate: 0.63,
    },
    WHO: {
      exceedances: 58,
      failure_rate: 1.82,
    },
    table: [],
  },
  PE: {
    description: `Prince Edward Island’s Housing Corporation conducted radon testing in 238 public housing units between 2008 and 2009. While only one per cent exceeded Health Canada’s guidelines, 11 per cent failed the WHO’s safety guideline of 100 Bq/m³.`,
    all_tests: 238,
    HC: {
      exceedances: 2,
      failure_rate: 0.84,
    },
    WHO: {
      exceedances: 27,
      failure_rate: 11.34,
    },
    table: [],
  },
  NB: {
    description: `14 per cent of radon tests from New Brunswick between 2008 and 2025 exceeded Health Canada’s guideline and more than 30 per cent failed the WHO standard.

    One 2018/19 test result from Moncton was 5,536 Bq/m³ –– nearly 28 times Health Canada’s safety guideline. 

    “If an atomic energy worker was exposed to that, (it) would be a serious violation of national guidelines,” says Dr. Aaron Goodarzi, a University of Calgary associate professor and director of Evict Radon, a national research project dedicated to understanding and preventing radon exposure. 

    It took the provincial department of Social Development at least two years to address the problem by installing a depressurization system that vents the radioactive gas outside of the home, according to government records.

    In a statement, the New Brunswick Housing Corporation said it "Corporation cares deeply about the health and safety of New Brunswickers, and it understands that exposure to radon gas is an important and pressing issue. That’s why we’re committed to testing all public housing units and remediating any that test above safe levels." 

    "Housing NB has deployed tests for radon gas in roughly 99 per cent of the province’s public housing and rural and Indigenous housing units," said spokesperson Adam Bowie. "Any unit that tested above acceptable levels is being remediated. That work will continue until it’s completed within the next year, with all costs for testing and remediation covered by Housing NB."
    `,
    all_tests: 4473,
    HC: {
      exceedances: 607,
      failure_rate: 13.57,
    },
    WHO: {
      exceedances: 1262,
      failure_rate: 28.21,
    },
    table: [
      ["Winter 2016/2017", 125, 52.8, 28.0],
      ["Winter 2017/2018", 126, 65.08, 34.92],
      ["Winter 2018/2019", 120, 55.0, 55.0],
      ["Winter 2019/2020", 144, 42.36, 16.67],
      ["Winter 2020/2021", 279, 38.71, 13.62],
      ["Winter 2021/2022", 417, 36.21, 13.43],
    ],
  },
  NL: {
    description: `In Newfoundland and Labrador, one tenant’s concerns about radon lead to a province-wide testing program. Janet Noftall, environmental manager for the Newfoundland and Labrador Housing Corporation (NLHC), recalls being approached by a tenant who bought themselves a radon test and found high levels.

    The tenant was a mother with two kids living in their basement, where radon is typically the highest asked, “was concerned about her kids’ health,”  Noftall said. 

    She asked, “what were we going to do with that information?”

    In response, NLHC swiftly tested and remediated the tenants housing unit in late 2022, and began rolling out a program to test the rest. They’ve now tested more than 123 homes, nearly 20 per cent of which exceeded Health Canada’s guideline. 

    “Radon gas is another factor in determining the health of those who occupy our homes,” Noftall said. “To me, it’s just one more element, something that we needed to do and should do.”
    `,
    all_tests: 123,
    HC: {
      exceedances: 23,
      failure_rate: 18.70,
    },
    WHO: {
      exceedances: null,
      failure_rate: null,
    },
    table: [],
  },
  AB: {
    description: `Alberta and the prairies have some of the highest documented residential radon levels in the world. 

    But the province’s seniors and housing ministry said it "does not have a dedicated program related to radon testing," in response to a formal freedom-of-information request for its test result data.
    
    “The health and safety of Albertans is a top priority for Alberta’s government and radon is among many environmental hazards we take seriously,” said Heather Barlow, press secretary for the Office of the Minister of Seniors, Community and Social Services.
    
    “Tenants concerned about radon levels in their homes are encouraged to contact their local housing operator or private landlord`,
    all_tests: 0,
    HC: {
      exceedances: 0,
      failure_rate: null,
    },
    WHO: {
      exceedances: 0,
      failure_rate: null,
    },
    table: [],
  },
  ON: {
    description: `Ontario has not conducted province-wide radon testing at social housing locations. Data is only available from select municiaplities whose houisng authorities conducted testing and provided their data: Simcoe county, Wellington and Kingston.`,
    all_tests: 1551,
    HC: {
      exceedances: 66,
      failure_rate: 4.26,
    },
    WHO: {
      exceedances: null,
      failure_rate: null,
    },
    table: [],
  },
  "N.W.T": {
    description: "",
    all_tests: 0,
    HC: {
      exceedances: 0,
      failure_rate: null,
    },
    WHO: {
      exceedances: 0,
      failure_rate: null,
    },
    table: [],
  },
  NU: {
    description: "",
    all_tests: 0,
    HC: {
      exceedances: 0,
      failure_rate: null,
    },
    WHO: {
      exceedances: 0,
      failure_rate: null,
    },
    table: [],
  },
};

const map_fullname_abbrv = {
  Simcoe: "Simcoe",
  Wellington: "Wellington",
  Kingston: "Kingston",
  "British Columbia": "BC",
  "Yukon Territory": "YK",
  "Nova Scotia": "NS",
  Manitoba: "MB",
  Saskatchewan: "SK",
  Montreal: "Montreal",
  Quebec: "QC",
  "Prince Edward Island": "PE",
  "New Brunswick": "NB",
  "Newfoundland and Labrador": "NL",
  Alberta: "AB",
  Ontario: "ON",
  "Northwest territories": "N.W.T",
  Nunavut: "NU",
};

let provinces = Object.keys(map_fullname_abbrv).filter(
  (key) => !who_hc_data[map_fullname_abbrv[key]]["HC"]["failure_rate"] == null || who_hc_data[map_fullname_abbrv[key]]["WHO"]["failure_rate"]
);


const map_abbrv_fullname = {
  Simcoe: "Simcoe",
  Wellington: "Wellington",
  Kingston: "Kingston",
  BC: "British Columbia",
  YK: "Yukon Territory",
  NS: "Nova Scotia",
  MB: "Manitoba",
  SK: "Saskatchewan",
  Montreal: "Montreal",
  QC: "Quebec",
  PE: "Prince Edward Island",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  AB: "Alberta",
  ON: "Ontario",
  "N.W.T": "Northwest territories",
  NU: "Nunavut",
};

const locations = {
  "B.C.": {
    "Interior B.C.": { lat: 49.8863, lon: -119.4966 },
    "Northern B.C.": { lat: 53.9171, lon: -122.7497 },
    "Vancouver Island": { lat: 49.6506, lon: -125.4494 },
  },
  Yukon: {
    Whitehorse: { lat: 60.7197, lon: -135.0523 },
    Carcross: { lat: 60.1676, lon: -134.7074 },
    Carmacks: { lat: 62.0877, lon: -136.292 },
    Dawson: { lat: 64.0639, lon: -139.4333 },
    Faro: { lat: 62.2316, lon: -133.3561 },
    "Haines Junction": { lat: 60.7525, lon: -137.5102 },
    Mayo: { lat: 63.5942, lon: -135.8966 },
    "Pelly Crossing": { lat: 67.5696, lon: -139.8288 },
    "Ross River": { lat: 67.5696, lon: -139.8288 },
    Teslin: { lat: 60.166, lon: -132.7247 },
    "Watson Lake": { lat: 60.063, lon: -128.7162 },
  },
  "Nova Scotia": {
    "Annapolis Co.": { lat: 44.8506, lon: -65.3481 },
    "Cape Breton Co.": { lat: 46.1368, lon: -60.1942 },
    "Colchester Co.": { lat: 45.3667, lon: -63.2667 },
    "Cumberland Co.": { lat: 45.6847, lon: -64.1985 },
    "Halifax Regional Municipality": { lat: 44.6488, lon: -63.5752 },
    "Kings Co.": { lat: 45.0807, lon: -64.4887 },
  },
  Manitoba: {
    "Brandon / Dauphin": { lat: 49.8485, lon: -99.9501 },
    "Central Park": { lat: 49.888, lon: -97.1445 },
    "Gilbert Park": { lat: 49.9069, lon: -97.1613 },
    "Lord Selkirk Park": { lat: 49.8923, lon: -97.1305 },
    "Northern Operations": { lat: 55.0, lon: -98.0 },
    "Selkirk / Portage": { lat: 50.2285, lon: -98.2791 },
    SouthWest: { lat: 49.5, lon: -99.0 },
    "Winnipeg South": { lat: 49.8283, lon: -97.15 },
    "Portage la Prairie": { lat: 49.9726, lon: -98.2836 },
  },
  Saskatchewan: {
    "Saskatoon": { "lat": 52.1579, "lon": -106.6702 },
    "Yorkton": { "lat": 51.2174, "lon": -102.4739 },
    "Air Ronge": { "lat": 55.0862, "lon": -105.3203 },
    "Beaval": { "lat": 55.1522, "lon": -107.6073 },
    "Bengough": { "lat": 49.3968, "lon": -105.1279 },
    "Buffalo Narrows": { "lat": 55.8628, "lon": -108.4795 },
    "Canora": { "lat": 51.6337, "lon": -102.4376 }
  },
  Ontario: {
    "Simcoe County": { lat: 44.4717, lon: -79.8297 },
    "Wellington County": { lat: 43.7803, lon: -80.5438 },
    Kingston: { lat: 44.2334, lon: -76.493 },

    // we have no data for these provinces
    // and they are cross-hatched in map
    // Ontario: { lat: 52.5925562, lon: -88.6887308 },
  },
  Quebec: {
    Montreal: { lat: 45.5019, lon: -73.5674 },
    Quebec: { lat: 48.6667, lon: -65.8333 },
  },
  "P.E.I.": {
    Charlottetown: { lat: 46.2382, lon: -63.1311 },
    Montague: { lat: 46.1691, lon: -62.6505 },
    Souris: { lat: 46.3548, lon: -62.2542 },
    Summerside: { lat: 46.3934, lon: -63.7902 },
    Kensington: { lat: 46.4369, lon: -63.6374 },
    Wellington: { lat: 46.4538, lon: -63.9995 },
  },
  "New Brunswick": {
    "Acadian Peninsula": { lat: 47.5237, lon: -65.2142 },
    Chaleur: { lat: 47.6208, lon: -65.6587 },
    Edmunston: { lat: 47.369, lon: -68.3267 },
    Fredericton: { lat: 47.369, lon: -68.3267 },
    Miramichi: { lat: 47.0296, lon: -65.5059 },
    Moncton: { lat: 46.0878, lon: -64.7782 },
    Restigouche: { lat: 47.768, lon: -66.6893 },
    "Saint John": { lat: 45.2733, lon: -66.0633 },
    Gagetown: { lat: 45.781, lon: -66.1528 },
    Bathurst: { lat: 47.6184, lon: -65.6513 },
    Sackville: { lat: 45.8979, lon: -64.3683 },
    "Saint-Antoine": { lat: 46.3629, lon: -64.7527 },
    Salisbury: { lat: 46.0261, lon: -65.0435 },
  },

  // we have no data for these provinces
  // and they are cross-hatched in map

  // "Newfoundland and Labrador": {
  //   "Newfoundland and Labrador": { lat: 53.82514578, lon: -62.43940838 },
  // },
  // Alberta: {
  //   Alberta: { lat: 55.001251, lon: -115.002136 },
  // },
  // "Northwest Territories": {
  //   "Northwest Territories": { lat: 63.88092825, lon: -119.8858698 },
  // },
  // Nunavut: {
  //   Nunavut: { lat: 63.96073239, lon: -98.29384469 },
  // },
};

// Convert the location object into an array for ECharts
const circleData = [];
Object.keys(locations).forEach((region) => {
  if (Array.isArray(locations[region])) {
    locations[region].forEach((loc) => {
      circleData.push([loc.lon, loc.lat, region]); // Longitude, Latitude, and Circle Size
    });
  } else {
    Object.keys(locations[region]).forEach((city) => {
      const loc = locations[region][city];
      circleData.push([loc.lon, loc.lat, city]); // Adjust size if needed
    });
  }
});

function CanadaMap({ mapType, collapse, setCollapse }) {
  const chartRef = useRef(null);
  const [selectedProvince, setSelectedProvince] = useState(null); // Track selected province

  const map_data = Object.keys(who_hc_data).map((key) => {
    const failureRate = who_hc_data[key][mapType]["failure_rate"];
    return {
      name: key,
      value: failureRate,
      label: ["Simcoe", "Kingston", "Wellington"].includes(key)
        ? { show: false }
        : {},
      itemStyle:
        failureRate == null
          ? {
              decal: {
                symbol: "line",
                dashArrayX: [4, 4],
                dashArrayY: [2, 2],
                color: "rgba(0,0,0,0.2)",
              },
              areaColor: "#ffffff",
            }
          : {},
      tooltip: {
        show: failureRate != null,
      },
      select:
        failureRate == null
          ? {
              itemStyle: {
                areaColor: "#ffffff",
                borderColor: "#D3D3D3",
              },
              label: {
                color: "black",
              },
            }
          : undefined,
      emphasis:
        failureRate == null
          ? {
              itemStyle: {
                areaColor: "#ffffff",
                borderColor: "#D3D3D3",
              },
              label: {
                color: "black",
              },
            }
          : undefined,
    };
  });

  const selectedData = selectedProvince ? who_hc_data[selectedProvince] : null;
  const whoDonut = selectedData
    ? buildDonutData(selectedData["WHO"]["failure_rate"], who_color)
    : null;
  const hcDonut = selectedData
    ? buildDonutData(selectedData["HC"]["failure_rate"], hc_color)
    : null;
  const showYearlyBreakdown =
    selectedData &&
    selectedData.table.length > 0 &&
    !["Wellington", "NB"].includes(selectedProvince);

  useEffect(() => {
    // Register the Canada map from the local file
    echarts.registerMap("Canada", MapCA);
    const chartInstance = echarts.init(chartRef.current);

    const options = {
      // set geo for scatter plot
      geo: {
        map: "Canada",
        roam: false,
      },
      title: {
        left: "center",
        textStyle: {
          color: PrimaryColor,
        },
      },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          // Extract the name and value from params
          if (params.seriesType === "map") {
            // Tooltip for the map
            const { name, value } = params;
            return `${map_abbrv_fullname[name]}: ${value}%`;
          }
        },
      },

      // set after clicked color for map
      // set to null if you don't want.
      select: {
        itemStyle: {
          areaColor: "#B09CB6", // green color when clicked (selected)
        },
      },

      visualMap: {
        // toggle to 2 in order to remove background colors of provinces.
        seriesIndex: 1, // in order not to affect scatters.
        left: "right",
        min: 0,
        max: 45, //mapType === "HC" ? 22 : 45,
        inRange: {
          color: map_gradient,
        },
        text: ["High", "Low"],
        calculable: true,
      },
      series: [
        {
          name: "Samples location",
          type: "scatter",
          coordinateSystem: "geo",
          data: circleData,
          symbolSize: 3,
          itemStyle: {
            color: "black", // Fill color
            opacity: 1,
          },
          tooltip: {
            trigger: "item",
            formatter: (params) => {
              // Tooltip for scatter circles
              return `${params.value[2]}`;
            },
          },
        },
        {
          name: mapType === "HC" ? "HC Exceedances" : "WHO Exceedances",
          type: "map",
          map: "Canada",
          roam: false,
          itemStyle: {
            normal: {
              borderColor: "#D3D3D3", // Gray border color
              borderWidth: 1, // Adjust the border width as needed
            },
          },
          label: {
            show: true,
            fontSize: 12,
          },

          // set color when hover, set to null if you don't want.
          emphasis: {
            label: {
              show: true,
              color: "black", // Hover label color
            },
            itemStyle: {
              areaColor: "#B09CB6", // Hover color
            },
          },
          data: map_data,
          markLine: {
            animation: false,
            symbol: ["none", "none"], // Use arrow at the end of the line
            label: {
              show: true,
              position: "end", // Position label at the end of the line
              fontSize: 14,
              color: "#000", // Label text color
              formatter: (params) => {
                // Format the label to show the coordinates of the line
                const name = params.data.name;
                return `${name}`; // Show the coordinates (rounded to 4 decimals)
              },
            },
            lineStyle: {
              type: "solid", // Line style (solid, dashed, etc.)
              color: "#000000", // Line color
              width: 1, // Line width
            },
            data: [
              // Markers for Simcoe, Wellington, and Kingston
              [
                {
                  name: "Simcoe",
                  coord: [-79.7038708, 44.5014007],
                  value: who_hc_data["Simcoe"][mapType]["failure_rate"],
                }, // Simcoe coordinates
                {
                  name: "Simcoe",
                  coord: [-83.7038708, 44.8014007],
                  value: who_hc_data["Simcoe"][mapType]["failure_rate"],
                }, // Move left by 0.3 degrees in longitude
              ],
              [
                {
                  name: "Wellington",
                  coord: [-80.4614994, 43.7590121],
                  value: who_hc_data["Wellington"][mapType]["failure_rate"],
                }, // Wellington coordinates
                {
                  name: "Wellington",
                  coord: [-88.4614994, 41.7590121],
                  value: who_hc_data["Wellington"][mapType]["failure_rate"],
                }, // Move left by 0.3 degrees in longitude
              ],
              [
                {
                  name: "Kingston",
                  coord: [-76.481323, 44.230687],
                  value: who_hc_data["Kingston"][mapType]["failure_rate"],
                }, // Kingston coordinates
                {
                  name: "Kingston",
                  coord: [-76.481323, 42.230687],
                  value: who_hc_data["Kingston"][mapType]["failure_rate"],
                }, // Move left by 0.3 degrees in longitude
              ],
              [
                {
                  name: "Montreal",
                  coord: [-73.5674, 45.5019],
                  value: who_hc_data["Montreal"][mapType]["failure_rate"],
                }, // Montreal  coordinates
                {
                  name: "Montreal",
                  coord: [-67.5674, 39.230687],
                  value: who_hc_data["Montreal"][mapType]["failure_rate"],
                },
              ],
            ],
          },
        },
      ],
    };

    // Set click event listener for provinces
    chartInstance.on("click", (params) => {
      if (
        (params.componentType === "series" && params.seriesType === "map") ||
        params.componentType === "markLine"
      ) {
        if (who_hc_data[params.name][mapType]["failure_rate"] == null) {
          return;
        }
        console.log(params.name);
        setSelectedProvince(params.name); // Update the selected province
        setCollapse(false);
      }
    });
    collapse = false;
    chartInstance.setOption(options);
    return () => {
      chartInstance.dispose();
    };
  }, [mapType, collapse]);

  return (
    <div>
      <div ref={chartRef} style={{ width: "100%", height: "400px" }} />
      <Typography.Text
        style={{
          color: text_light_gray,
          fontSize: "0.75rem",
          display: "block",
        }}
      >
        * Regions with lack of data are displayed with a hatched (---) pattern.
      </Typography.Text>
      <Typography.Text
        style={{
          color: text_light_gray,
          fontSize: "0.75rem",
          display: "block",
        }}
      >
        * Each dot represents a sample collected from the surrounding province.
      </Typography.Text>
      {!collapse && selectedProvince && (
        <div>
          <Divider />
          <Row justify="center" style={{ marginTop: -40, marginBottom: 0 }}>
            <Typography.Title level={3}>
              {map_abbrv_fullname[selectedProvince]}
              <sup>*</sup>
            </Typography.Title>
          </Row>
          <Row justify="center" style={{ marginTop: 0, marginBottom: 0 }}>
            <table
              style={{
                borderCollapse: "collapse",
                tableLayout: "auto",
                margin: "auto",
              }}
            >
              <tr style={{ color: "RGB(156,156,156)" }}>
                <td
                  style={{
                    color: text_dark_gray,
                    paddingRight: 10,
                  }}
                >
                  TOTAL TESTS
                </td>
                <td
                  style={{
                    color: text_dark_gray,
                    paddingRight: 10,
                    paddingLeft: 10,
                    borderRight: "1px solid #dddddd",
                    borderLeft: "1px solid #dddddd",
                    verticalAlign: "top",
                  }}
                >
                  TESTS THAT EXCEED WHO
                </td>
                <td
                  style={{
                    color: text_dark_gray,
                    paddingLeft: 10,
                    verticalAlign: "top",
                  }}
                >
                  WHO FAILURE RATE
                </td>
                <td
                  style={{
                    color: text_dark_gray,
                    paddingRight: 10,
                    paddingLeft: 10,
                    borderRight: "1px solid #dddddd",
                    borderLeft: "1px solid #dddddd",
                    verticalAlign: "top",
                  }}
                >
                  TESTS THAT EXCEED HC
                </td>
                <td
                  style={{
                    color: text_dark_gray,
                    paddingLeft: 10,
                    verticalAlign: "top",
                  }}
                >
                  HC FAILURE RATE
                </td>
              </tr>
              <tr style={{ fontSize: "2rem", color: PrimaryColor }}>
                <td style={{}}>
                  {formatNumberWithCommas(
                    who_hc_data[selectedProvince]["all_tests"] || 0
                  )}
                </td>
                <td
                  style={{
                    paddingLeft: 10,
                    borderRight: "1px solid #dddddd",
                    borderLeft: "1px solid #dddddd",
                    color: getExceedanceColor(
                      who_hc_data[selectedProvince]["WHO"]["exceedances"]
                    ),
                  }}
                >
                  {formatCount(
                    who_hc_data[selectedProvince]["WHO"]["exceedances"]
                  )}
                </td>
                <td
                  style={{
                    paddingLeft: 10,
                    color: getExceedanceColor(
                      who_hc_data[selectedProvince]["WHO"]["exceedances"]
                    ),
                  }}
                >
                  {formatPercent(
                    who_hc_data[selectedProvince]["WHO"]["failure_rate"]
                  )}
                </td>
                <td
                  style={{
                    paddingLeft: 10,
                    borderRight: "1px solid #dddddd",
                    borderLeft: "1px solid #dddddd",
                    color: getExceedanceColor(
                      who_hc_data[selectedProvince]["HC"]["exceedances"]
                    ),
                  }}
                >
                  {formatCount(
                    who_hc_data[selectedProvince]["HC"]["exceedances"]
                  )}
                </td>
                <td
                  style={{
                    paddingLeft: 10,
                    color: getExceedanceColor(
                      who_hc_data[selectedProvince]["HC"]["exceedances"]
                    ),
                  }}
                >
                  {formatPercent(
                    who_hc_data[selectedProvince]["HC"]["failure_rate"]
                  )}
                </td>
              </tr>
            </table>
          </Row>
          <Divider />
          <Row gutter={[16, 16]} style={{ marginTop: -40, marginBottom: 10 }}>
            <Col span={12}>
              {/* <Typography.Title level={5}>WHO</Typography.Title> */}
              <DonutChart
                title={{ main: "WHO", sub: "Exceedances" }}
                data={whoDonut.data}
                colors={whoDonut.colors}
                centerText={whoDonut.centerText}
                show_legend={true}
              />
            </Col>
            <Col span={12}>
              {/* <Typography.Title level={5}>Health Canada</Typography.Title> */}
              <DonutChart
                title={{ main: "Health Canada", sub: "Exceedances" }}
                data={hcDonut.data}
                colors={hcDonut.colors}
                centerText={hcDonut.centerText}
                show_legend={true}
              />
            </Col>
          </Row>
          {showYearlyBreakdown && (
            <>
              <Divider style={{ marginTop: -30, marginBottom: 10 }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  {[
                    {
                      c1: "Year",
                      c2: "Total Tests",
                      c3: "HC Failures",
                      c4: "WHO Failures",
                    },
                    ...Object.keys(who_hc_data[selectedProvince]["table"]).map(
                      (index) => {
                        const item =
                          who_hc_data[selectedProvince]["table"][index];
                        return {
                          c1: item[0],
                          c2: formatNumberWithCommas(item[1]),
                          c3: item[3] + "%",
                          c4: item[2] + "%",
                        };
                      }
                    ),
                  ].map((x, i) => (
                    <tr
                      style={{
                        borderBottom: "1px solid #dddddd",
                        color: text_dark_gray, //i ? text_dark_gray : text_title_gold,
                      }}
                    >
                      <td
                        style={{
                          paddingLeft: 6,
                          width: "25%",
                          verticalAlign: "bottom",
                        }}
                      >
                        {x.c1}
                      </td>
                      <td
                        style={{
                          paddingLeft: 6,
                          width: "25%",
                          verticalAlign: "bottom",
                        }}
                      >
                        {x.c2}
                      </td>
                      <td
                        style={{
                          paddingLeft: 6,
                          width: "25%",
                          verticalAlign: "bottom",
                          backgroundColor: getColorForFailure(x.c3, hc_color),
                        }}
                      >
                        {x.c3}
                      </td>
                      <td
                        style={{
                          paddingLeft: 6,
                          width: "25%",
                          verticalAlign: "bottom",
                          backgroundColor: getColorForFailure(x.c4, who_color),
                        }}
                      >
                        {x.c4}
                      </td>
                    </tr>
                  ))}
                </table>
              </div>
              <Divider style={{ marginTop: 0, marginBottom: 10 }} />
              <Row
                justify="center"
                style={{ marginTop: -20, marginBottom: -50 }}
              >
                <Typography.Title level={5}>
                  Yearly Failure distribution
                </Typography.Title>
              </Row>
              <LineScatterChart
                table={who_hc_data[selectedProvince]["table"]}
              />
            </>
          )}

          {who_hc_data[selectedProvince]["description"] != "" && (
            <>
              <Divider style={{ marginTop: -20, marginBottom: 10 }} />
              <Typography.Paragraph
                style={{
                  fontSize: "14px", // same as the "Total Tests"
                  color: "black", // text_dark_gray,
                }}
              >
                {/* <span style={{ fontSize: "1rem", marginRight: "4px" }}></span> */}
                {who_hc_data[selectedProvince]["description"]}
              </Typography.Paragraph>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// function DonutChart({ data, title, colors, show_legend = false }) {
//   const chartRef = useRef(null);

//   useEffect(() => {
//     const chartInstance = echarts.init(chartRef.current);

//     const options = {
//       title: {
//         text: title,
//         left: "center",
//       },
//       tooltip: {
//         show: true,
//         trigger: "axis",
//       },
//       legend: show_legend
//         ? {
//             orient: "vertical",
//             left: "left",
//           }
//         : undefined,
//       series: [
//         {
//           name: "Metrics",
//           type: "pie",
//           radius: ["30%", "60%"], // Donut chart
//           avoidLabelOverlap: true,
//           label: {
//             show: true,
//             position: "inside", // Show labels outside
//             formatter: "{c} %", // Display percentage with the name
//             fontSize: 12,
//           },
//           emphasis: {
//             label: {
//               show: true,
//               fontSize: "16",
//               fontWeight: "bold",
//             },
//           },
//           labelLine: {
//             show: true, // Show lines connecting labels to the chart
//           },
//           data: data,
//           // Hardcoded colors for the metrics
//           color: colors,
//         },
//       ],
//     };

//     chartInstance.setOption(options);

//     return () => {
//       chartInstance.dispose(); // Clean up the chart instance on unmount
//     };
//   }, [data, title]);

//   return <div ref={chartRef} style={{ width: "100%", height: "400px" }} />;
// }

function DonutChart({ data, title, colors, show_legend = false, centerText }) {
  const chartRef = useRef(null);

  useEffect(() => {
    const chartInstance = echarts.init(chartRef.current);
    const defaultCenterText =
      centerText == null ? `${data[0]["value"]}%` : centerText;

    // Initial options for the donut chart
    const options = {
      title: {
        text: title["main"],
        subtext: title["sub"],
        left: "center",
        top: "45px",
        textStyle: {
          fontSize: 18, // Change the font size here
          // color: "#000", // Optional: Set the title color
        },
      },
      tooltip: {
        show: false, // Disable the default tooltip
      },
      legend: show_legend
        ? {
            orient: "vertical",
            // left: "left",
            bottom: "20px",
            padding: [30, 30],
          }
        : undefined,
      series: [
        {
          name: "Metrics",
          type: "pie",
          radius: ["40%", "60%"], // Donut chart
          avoidLabelOverlap: true,
          label: {
            show: false, // Initially hide all labels
          },
          emphasis: {
            label: {
              show: false, // No labels on emphasis
            },
          },
          labelLine: {
            show: false, // Hide connecting lines
          },
          data: data,
          color: colors,
        },
      ],
      graphic: {
        type: "text",
        left: "center",
        top: "middle",
        style: {
          fontSize: 18,
          textAlign: "center",
          fill: "#000",
          text: defaultCenterText,
        },
      },
    };

    chartInstance.setOption(options);

    // Add event listeners for hovering on segments
    chartInstance.on("mouseover", (params) => {
      if (centerText != null) {
        return;
      }
      if (params.seriesType === "pie") {
        chartInstance.setOption({
          graphic: {
            style: {
              fontSize: 18,
              text: `${params.data["value"]}%`, // Display the percentage in the center
            },
          },
        });
      }
    });

    // Clear the center text when the mouse leaves the segment
    chartInstance.on("mouseout", () => {
      chartInstance.setOption({
        graphic: {
          style: {
            fontSize: 18,
            text: defaultCenterText,
          },
        },
      });
    });

    return () => {
      chartInstance.dispose(); // Clean up the chart instance on unmount
    };
  }, [data, title, colors, show_legend, centerText]);

  return <div ref={chartRef} style={{ width: "100%", height: "400px" }} />;
}

function App() {
  const [mapType, setMapType] = useState("HC"); // Track the selected radio option
  const [collapse, setCollapse] = useState(true);
  const handleRadioChange = (e: any) => {
    setMapType(e.target.value); // Update mapType based on the selected radio button
  };

  const [activeKey, setActiveKey] = useState("1");
  const [selectedProvince1, setSelectedProvince1] = useState(null);
  const [selectedProvince2, setSelectedProvince2] = useState(null);

  const handleTabChange = (key) => {
    setActiveKey(key);
    if (key === "2") {
      setSelectedProvince1(null);
      setSelectedProvince2(null); // Reset selections when switching to Tab 2
      setCollapse(true);
      setMapType("HC");
    }
  };

  const availableProvinces1 = provinces.filter(
    (province) => province !== selectedProvince2
  );
  const availableProvinces2 = provinces.filter(
    (province) => province !== selectedProvince1
  );

  const selectedProvince1Key = selectedProvince1
    ? map_fullname_abbrv[selectedProvince1]
    : null;
  const selectedProvince2Key = selectedProvince2
    ? map_fullname_abbrv[selectedProvince2]
    : null;

  const whoComparison1 = selectedProvince1Key
    ? buildDonutData(
        who_hc_data[selectedProvince1Key]["WHO"]["failure_rate"],
        who_color
      )
    : null;
  const whoComparison2 = selectedProvince2Key
    ? buildDonutData(
        who_hc_data[selectedProvince2Key]["WHO"]["failure_rate"],
        who_color
      )
    : null;
  const hcComparison1 = selectedProvince1Key
    ? buildDonutData(
        who_hc_data[selectedProvince1Key]["HC"]["failure_rate"],
        hc_color
      )
    : null;
  const hcComparison2 = selectedProvince2Key
    ? buildDonutData(
        who_hc_data[selectedProvince2Key]["HC"]["failure_rate"],
        hc_color
      )
    : null;

  return (
    <Flex
      gap="middle"
      vertical
      justify="center"
      ghost
      align="center"
      style={{
        padding: 8,
        // backgroundRepeat: 'repeat',
        // backgroundSize: 'auto',
        // backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 75.338 64.047'%3E%3Cpath d='M58.91.129c-7.243 1.392-4.612 13.171 2.381 11.883C68.741 10.64 66.682-1.365 58.91.129M14.989.924C8.727 2.05 10.4 12.193 16.841 11.03 22.671 9.978 21.118-.18 14.99.924m15.875 9.275c-6.677 1.543-4.279 12.277 2.117 10.832 7.642-1.726 5.455-12.582-2.117-10.832m38.1 9.79c-6.824 1.566-4.162 11.957 2.646 10.302 6.474-1.574 3.818-11.786-2.646-10.302m-21.696 2.117c-6.069 1.444-4.123 12.012 2.117 10.526 6.181-1.472 4.476-12.094-2.117-10.526M3.876 24.223c-5.689 1.38-5.083 11.781 1.587 10.34 6.348-1.373 5.598-12.084-1.587-10.34m21.431 7.408c-8.02 1.911-7.732 16.363 1.852 13.788 6.786-1.823 7.46-16.006-1.852-13.788m19.579 14.817c-6.124 1.488-5.568 12.134 1.323 10.526 6.927-1.616 5.945-12.292-1.323-10.526m-32.279 5.556c-6.851 1.55-6.849 13.514.794 11.893 6.066-1.287 6.484-13.54-.794-11.893z' fill='%2E7E8E5' fill-opacity='0.025' /%3E%3C/svg%3E")`
      }}
    >
      <Card style={{ marginTop: 24, maxWidth: 650 }}>
        <Flex
          gap="small"
          size="small"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Left PNG Image */}
          <img
            src="/images/condo.png"
            alt="Left Image"
            style={{ width: "50px", height: "50px" }}
          />

          {/* Text Content */}
          <div style={{ marginLeft: 15 }}>
            <Typography.Title level={4} style={{ color: text_title_gold }}>
              The Invisible Threat in Canada's Public Housing
            </Typography.Title>
            <Typography.Paragraph
              level={5}
              style={{ color: text_light_gray, fontSize: "0.85rem" }}
            >
              Exceedances are based on Health Canada’s national safety guideline
              of 200 becquerels per cubic metre (Bq/m³) and the World Health
              Organization’s (WHO) stricter guideline of 100 Bq/m³.
            </Typography.Paragraph>
          </div>

          {/* Right PNG Image */}
          <img
            src="/images/radon.png"
            alt="Right Image"
            style={{ width: "50px", height: "50px" }}
          />
        </Flex>

        <Tabs
          activeKey={activeKey}
          onChange={handleTabChange}
          type="card"
          size="large"
        >
          <TabPane tab="Map View" key="1">
            <Radio.Group onChange={handleRadioChange} value={mapType}>
              <Radio value="HC">Health Canada Exceedances</Radio>
              <Radio value="WHO">WHO Exceedances</Radio>
            </Radio.Group>
            <CanadaMap
              mapType={mapType}
              collapse={collapse}
              setCollapse={setCollapse}
            />
          </TabPane>

          <TabPane tab="Comparison" key="2">
            <Row gutter={[16, 16]} style={{}}>
              <Col span={12}>
                <Typography.Text>Select Jurisdiction 1</Typography.Text>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select a jurisdiction"
                  value={selectedProvince1}
                  onChange={setSelectedProvince1}
                  allowClear
                  placement="bottomLeft"
                >
                  {availableProvinces1.map((province) => (
                    <Option key={province} value={province}>
                      {province}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={12}>
                <Typography.Text>Select Jurisdiction 2</Typography.Text>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select a jurisdiction"
                  value={selectedProvince2}
                  onChange={setSelectedProvince2}
                  allowClear
                >
                  {availableProvinces2.map((province) => (
                    <Option key={province} value={province}>
                      {province}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            {selectedProvince1 && selectedProvince2 && (
              <Row justify="center" style={{ marginTop: 0, marginBottom: 0 }}>
                <Typography.Title level={4}>
                  WHO Exceedances
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginLeft: "10px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "12px",
                        height: "12px",
                        backgroundColor: who_color,
                        marginRight: "5px",
                      }}
                    ></span>
                    <span>yes</span>
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginLeft: "10px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "12px",
                        height: "12px",
                        backgroundColor: no_color,
                        marginRight: "5px",
                      }}
                    ></span>
                    <span>no</span>
                  </span>
                </Typography.Title>
              </Row>
            )}
            {selectedProvince1 && selectedProvince2 && (
              <Divider style={{ marginTop: 0, marginBottom: 0 }} />
            )}
            {selectedProvince1 && selectedProvince2 && (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  {/* <Typography.Title level={5}>
                    {selectedProvince1}
                  </Typography.Title> */}
                  <DonutChart
                    show_legend={false}
                    data={whoComparison1.data}
                    colors={whoComparison1.colors}
                    centerText={whoComparison1.centerText}
                    title={{ main: selectedProvince1, sub: "Exceedances" }}
                  />
                </Col>
                <Col span={12}>
                  {/* <Typography.Title level={5}>
                    {selectedProvince2}
                  </Typography.Title> */}
                  <DonutChart
                    data={whoComparison2.data}
                    colors={whoComparison2.colors}
                    centerText={whoComparison2.centerText}
                    title={{ main: selectedProvince2, sub: "Exceedances" }}
                  />
                </Col>
              </Row>
            )}
            {selectedProvince1 && selectedProvince2 && (
              <Row
                justify="center"
                style={{ marginTop: -100, marginBottom: 0 }}
              >
                {/* <Typography.Title level={4}>
                  Health Canada Exceedances
                </Typography.Title> */}
                <Typography.Title level={4}>
                  Health Canada Exceedances
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginLeft: "10px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "12px",
                        height: "12px",
                        backgroundColor: hc_color,
                        marginRight: "5px",
                      }}
                    ></span>
                    <span>yes</span>
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginLeft: "10px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "12px",
                        height: "12px",
                        backgroundColor: no_color,
                        marginRight: "5px",
                      }}
                    ></span>
                    <span>no</span>
                  </span>
                </Typography.Title>
              </Row>
            )}
            {selectedProvince1 && selectedProvince2 && (
              <Divider style={{ marginTop: 0, marginBottom: 0 }} />
            )}
            {selectedProvince1 && selectedProvince2 && (
              <Row
                gutter={[16, 16]}
                style={{ marginTop: 0, marginBottom: -100 }}
              >
                <Col span={12}>
                  {/* <Typography.Title level={5}>
                    {selectedProvince1}
                  </Typography.Title> */}
                  <DonutChart
                    show_legend={false}
                    data={hcComparison1.data}
                    colors={hcComparison1.colors}
                    centerText={hcComparison1.centerText}
                    title={{ main: selectedProvince1, sub: "Exceedances" }}
                  />
                </Col>
                <Col span={12}>
                  {/* <Typography.Title level={5}>
                    {selectedProvince2}
                  </Typography.Title> */}
                  <DonutChart
                    data={hcComparison2.data}
                    colors={hcComparison2.colors}
                    centerText={hcComparison2.centerText}
                    title={{ main: selectedProvince2, sub: "Exceedances" }}
                  />
                </Col>
              </Row>
            )}
          </TabPane>
        </Tabs>
      </Card>
      <Space>
      <a href="https://www.hivelab-uoft.ca" target="_blank" rel="noopener noreferrer">
        <HiveLogo style={{ textAlign: "center", marginTop: 6 }} />
      </a>
        <Text
          style={{
            fontSize: 10,
            color: "RGB(156,156,156)",
            textAlign: "center",
            marginTop: -6,
          }}>
            Developed by{" "}
            <a
              href="https://www.hivelab-uoft.ca"
              target="_blank"
              rel="noopener noreferrer"
            >
              HIVE LAB
            </a>
        </Text>
      </Space>
    </Flex>
  );
}

export default App;
